import { useNavigate } from "react-router-dom";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { useAppSelector } from "app/hooks";
import { announcement } from "app/themes/colors";
import { selectCurrentUser } from "features/auth/auth-slice";
import { useDismissibleBanner } from "hooks/use-dismissible-banner.hook";

import {
  MENTIONS_LAUNCH_KEYS,
  MENTION_EMAIL_SETTING_LINK
} from "./mentions-launch";
import useMentionsLaunchUserId from "./use-mentions-launch-user-id.hook";

/**
 * A one-time tip at the top of the bell's Mentions tab: this is where mentions land, and the
 * email is off until the person turns it on. "Turn on email" opens Personal Settings →
 * Notification Preferences with the mention email checkbox highlighted. Flag off: nothing.
 */
export default function MentionsBellTip({
  onNavigate
}: {
  /** Called before leaving for settings, so the bell drawer can close. */
  onNavigate: () => void;
}) {
  const userId = useMentionsLaunchUserId();
  return userId ? <Tip userId={userId} onNavigate={onNavigate} /> : null;
}

function Tip({
  userId,
  onNavigate
}: {
  userId: string;
  onNavigate: () => void;
}) {
  const navigate = useNavigate();
  const emailOn = Boolean(
    useAppSelector(selectCurrentUser)?.mention_email_notifications_enabled
  );
  const { visible, dismiss } = useDismissibleBanner({
    syncAcrossInstances: true,
    storageKey: MENTIONS_LAUNCH_KEYS.bellTip,
    userId,
    datadogActions: {
      shown: "mentions_bell_tip_shown",
      dismissed: "mentions_bell_tip_dismissed"
    }
  });

  if (!visible) return null;

  return (
    <Box
      role="status"
      data-testid="mentions-bell-tip"
      sx={{
        backgroundColor: announcement.background,
        border: `1px solid ${announcement.border}`,
        borderRadius: "8px",
        px: 2,
        py: 1.5,
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5
      }}
    >
      <InfoOutlinedIcon
        aria-hidden="true"
        sx={{
          fontSize: 20,
          color: announcement.accent,
          mt: "1px",
          flexShrink: 0
        }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          component="p"
          variant="body2"
          sx={{ color: announcement.text, lineHeight: 1.5 }}
        >
          <strong>This is where mentions land.</strong> When a teammate mentions
          you in a chart note or a task, it shows up here with a link straight
          to it.
          {emailOn
            ? " You also get an email for each one."
            : " Want an email too? It's off until you turn it on."}
        </Typography>
        <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
          {!emailOn && (
            <TipButton
              testId="mentions-bell-tip-email"
              onClick={() => {
                dismiss({ source: "turn_on_email" });
                onNavigate();
                navigate(MENTION_EMAIL_SETTING_LINK);
              }}
            >
              Turn on email
            </TipButton>
          )}
          <TipButton
            testId="mentions-bell-tip-got-it"
            onClick={() => dismiss({ source: "got_it" })}
          >
            Got it
          </TipButton>
        </Stack>
      </Box>
    </Box>
  );
}

function TipButton({
  onClick,
  testId,
  children
}: {
  onClick: () => void;
  testId: string;
  children: string;
}) {
  return (
    <Box
      component="button"
      type="button"
      data-testid={testId}
      onClick={onClick}
      sx={{
        background: "none",
        border: "none",
        color: announcement.accent,
        fontSize: 14,
        fontWeight: 700,
        fontFamily: (theme) => theme.typography.fontFamily,
        cursor: "pointer",
        p: 0,
        whiteSpace: "nowrap",
        "&:hover": { textDecoration: "underline" },
        "&:focus-visible": {
          outline: `2px solid ${announcement.accent}`,
          outlineOffset: 2
        }
      }}
    >
      {children}
    </Box>
  );
}
